import { HttpClient } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ProfileModel } from '../models/profile.model';
import { ContactUser, Login, LoginResponse, MailUpdatePayload, SendPassRecoveryPayload, UserIpData, UsersPayload, UsersPayloadKa, UsersResponse } from '../models/users.model';
import { LocalStorageService } from './local-storage.service';

@Injectable({
	providedIn: 'root',
})
export class UsersService {
	static loggedOut: EventEmitter<any> = new EventEmitter<any>();
	static isLPDEALER: EventEmitter<any> = new EventEmitter<any>();
	private _registerUser = new BehaviorSubject<any>(null);
	private ipUrl = 'https://httpbin.org/ip';
	public clientUserId: any = '';

	constructor(
		private readonly http: HttpClient,
		private localStorage: LocalStorageService
	) {
		this.clientUserId = localStorage.getItem('ROD-register.clientUserId');
	}

	get user() {
		let user: any = this.localStorage.getItem('ROD-logged.user');
		if (user !== undefined && user !== null && typeof user === 'string') {
			user = JSON.parse(user);
		}
		return user;
	}

	get userId() {
		let user: any = this.localStorage.getItem('ROD-logged.user');
		let clientDealer: any = this.localStorage.getItem('ROD-logged.clientUser') || this.localStorage.getItem('ROD-register.clientUser');
		if (clientDealer !== undefined && clientDealer !== null) {
			clientDealer = JSON.parse(clientDealer);
			return clientDealer?.authContext?.id || clientDealer?.id || clientDealer?.data?.id;
		} else if (user !== undefined && user !== null && typeof user === 'string') {
			user = JSON.parse(user);
			return user?.authContext?.id;
		}
	}

	getRegisterUser(): Observable<UsersResponse> {
		return this._registerUser.asObservable();
	}

	setRegisterUser(registerUser: UsersResponse): void {
		this._registerUser.next(registerUser);
	}

	get registerUser(): UsersResponse {
		return this._registerUser.value;
	}

	getUserIp() {
		return this.http.get<{ origin: string }>(this.ipUrl);
	}

	sendUserIp(payload: UserIpData) {
		return this.http.post<UserIpData>(`${environment.apiUrl}ip`, payload);
	}

	login(payload: Login) {
		return this.http.post<LoginResponse>(`${environment.apiUrl}authentications`, payload);
	}

	dealerLogin(token: string) {
		return this.http.get<any>(`${environment.apiUrl}authentications/dealerships/tokens/${token}`);
	}

	getUser() {
		return this.http.get<any>(`${environment.apiUrl}users/logged-users`);
	}

	getClientsByIds(ids: number[]) {
		return this.http.get<any>(`${environment.apiUrl}users/by-ids?ids=${ids.join(',')}`);
	}

	switchToOwnedClient(clientId: number) {
    return this.http.post<any>(`${environment.apiUrl}users/owned-clients/${clientId}/access`, {});
	}

	getAllClients(page: number = 1, pageSize: number = 5, search: string = '') {
    const params = `page=${page}&pageSize=${pageSize}&search=${encodeURIComponent(search)}`;
    return this.http.get<any>(`${environment.apiUrl}users/all-clients?${params}`);
	}

	getAuthenticatedUser() {
		// Obtiene el rol del token JWT validado por el backend
		// Este endpoint requiere [Authorize] y lee el rol del token JWT (no puede ser modificado)
		return this.http.get<any>(`${environment.apiUrl}authentications/users`);
	}

	getUserById(id: string | any) {
		return this.http.get<any>(`${environment.apiUrl}users/${id}`);
	}

	getAdditionalFieldsUserById(id: string | any) {
		return this.http.get<any>(`${environment.apiUrl}users/${id}/pep-forms`);
	}

	accessLink(dealershipId: any, payload: any) {
		return this.http.post<any>(`${environment.apiUrl}authentications/dealerships/${dealershipId}/access-links`, payload);
	}

	createUser(payload: UsersPayload) {
		return this.http.post<UsersResponse>(`${environment.apiUrl}users`, payload);
	}

	createUserKa(payload: UsersPayloadKa) {
		return this.http.post<UsersResponse>(`${environment.apiUrl}users/user-ka`, payload);
	}

	updatePreRegistrationStep(payload: any) {
		return this.http.put<any>(`${environment.apiUrl}users/update-pre-registration-step`, payload);
	}

	updateContact(id: number, payload: ContactUser) {
		return this.http.put<any>(`${environment.apiUrl}users/${id}/contacts`, { contact: payload });
	}

	deleteContact(id: number, idContact: number) {
		return this.http.put<any>(`${environment.apiUrl}users/${id}/delete-contact/${idContact}`, {});
	}

	verifyPin(email: string, pin: string) {
		return this.http.put<any>(`${environment.apiUrl}users/emails/verifications`, { email: email, pin: pin });
	}

	resendVerifications(id: string) {
		return this.http.post<any>(`${environment.apiUrl}users/${id}/emails/resend-verifications`, {});
	}

	sendPassRecovery(payload: SendPassRecoveryPayload) {
		return this.http.post<any>(`${environment.apiUrl}users/recoveries/passwords/emails`, payload);
	}

	setNewPass(token: string, payload: SendPassRecoveryPayload) {
		return this.http.put<any>(`${environment.apiUrl}users/recoveries/passwords?token=${token}`, payload);
	}

	updateUser(payload: ProfileModel) {
		return this.http.put<any>(`${environment.apiUrl}users/${this.userId}`, payload);
	}

	updateEmail(id: string, payload: MailUpdatePayload) {
		return this.http.put<any>(`${environment.apiUrl}users/${id}/emails`, payload);
	}

	updatePersonalData(payload: any, userId?: any) {
		return this.http.put<any>(`${environment.apiUrl}users/${userId ? userId : this.userId}/personal-data`, payload);
	}

	updateAdditionalFields(payload: any, userId?: any) {
		return this.http.put<any>(`${environment.apiUrl}users/${userId ? userId : this.userId}/additional-fields`, payload);
	}

	updateCompanyData(payload: any, userId?: any) {
		return this.http.put<any>(`${environment.apiUrl}users/${userId ? userId : this.userId}/company-data`, payload);
	}

	updatePartners(payload: any, userId?: any) {
		return this.http.put<any>(`${environment.apiUrl}users/${userId ? userId : this.userId}/partners`, payload);
	}

	updateShareholdersData(payload: any, userId?: any) {
		return this.http.put<any>(
			`${environment.apiUrl}users/${userId ? userId : this.userId}/shareholders-data`,
			payload
		);
	}

	getShareholders(userId: string | number) {
		return this.http.get<any>(`${environment.apiUrl}users/${userId}/shareholders`);
	}

	createShareholder(userId: string | number, payload: any) {
		return this.http.post<any>(`${environment.apiUrl}users/${userId}/shareholders`, payload);
	}

	deleteShareholder(userId: string | number, shareholderId: number) {
		return this.http.delete<any>(`${environment.apiUrl}users/${userId}/shareholders/${shareholderId}`);
	}

	updateAdresses(payload: any, userId?: any) {
		return this.http.put<any>(`${environment.apiUrl}users/${userId ? userId : this.userId}/adresses`, payload);
	}

	updateDocuments(payload: any, userId?: any) {
		return this.http.put<any>(`${environment.apiUrl}users/${userId ? userId : this.userId}/documents`, payload);
	}

	finishRegister(userId?: any) {
		return this.http.post<any>(`${environment.apiUrl}users/${userId && userId !== null ? userId : this.userId}/finish-registrations`, {});
	}

	getUserByDocument(document: string) {
		return this.http.get<any>(`${environment.apiUrl}users/document/${document}`);
	}

	newGuid() {
		return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
			var r = (Math.random() * 16) | 0,
				v = c == 'x' ? r : (r & 0x3) | 0x8;
			return v.toString(16);
		});
	}
	refreshToken(): Observable<any> {
  return this.http.post(`${environment.apiUrl}authentications/refresh-token`, {});
	}
	logOut() {
		return this.http.get<any>(`${environment.apiUrl}authentications/logout`);
	}
}
